import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Eye, EyeOff, ArrowRight, ShieldCheck, FileCheck, Sparkles, Lock, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_prep-reviewer-login/artifacts/dzc1abq3_PrepHacker_Featured_Logo_Final.png";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [errorKind, setErrorKind] = useState(""); // "locked" | "warn" | "error"

  if (user && user !== false) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setErrorKind("");
    setLoading(true);
    const result = await login(email.trim(), password);
    setLoading(false);
    if (result.ok) {
      toast.success(`Welcome back, ${result.user.name || result.user.email}`);
      navigate("/dashboard");
      return;
    }
    const msg = result.error || "Login failed";
    setError(msg);
    if (result.status === 423) {
      setErrorKind("locked");
    } else if (/attempt remaining/i.test(msg)) {
      setErrorKind("warn");
    } else {
      setErrorKind("error");
      toast.error(msg);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* LEFT — Brand panel */}
      <div className="login-bg relative hidden lg:flex flex-col justify-between p-12 text-white overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />

        <div className="relative z-10 flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-black/40 backdrop-blur-md ring-1 ring-white/10 flex items-center justify-center overflow-hidden">
            <img src={LOGO_URL} alt="PrepHacker" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="text-base font-bold tracking-tight" style={{ fontFamily: "Outfit", color: "#ffffff" }}>
              PrepHacker-RN
            </div>
            <div className="text-[11px] font-mono uppercase tracking-wider" style={{ color: "#94a3b8" }}>
              Content Hub
            </div>
          </div>
        </div>

        <div className="relative z-10 max-w-lg">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-[11px] font-mono uppercase tracking-wider text-orange-300 mb-6">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Internal Platform</span>
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight"
            style={{ fontFamily: "Outfit", color: "#ffffff" }}
          >
            Build Better
            <br />
            <span style={{ color: "#fb923c" }}>Question Banks.</span>
          </h1>
          <p className="mt-6 text-base leading-relaxed max-w-md" style={{ color: "#cbd5e1" }}>
            A single hub to upload, review, QC and tag NCLEX-style content — with SME workflows
            built for medical and academic teams who care about clinical accuracy.
          </p>

          <div className="mt-10 grid grid-cols-1 gap-3 max-w-sm">
            {[
              { Icon: FileCheck, label: "Batch assignment & SME review" },
              { Icon: ShieldCheck, label: "Role-based access & audit trail" },
            ].map(({ Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.04] border border-white/10 backdrop-blur-sm"
              >
                <div className="w-8 h-8 rounded-lg bg-orange-500/15 text-orange-300 flex items-center justify-center">
                  <Icon className="w-4 h-4" />
                </div>
                <span className="text-sm text-slate-200">{label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-xs text-slate-400 font-mono">
          © {new Date().getFullYear()} PrepHacker. Internal Use Only.
        </div>
      </div>

      {/* RIGHT — Form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#f7f9fb]">
        <div className="w-full max-w-md">
          {/* mobile logo */}
          <div className="lg:hidden mb-8 flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center overflow-hidden">
              <img src={LOGO_URL} alt="PrepHacker" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
                PrepHacker
              </div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
                Content Hub
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl brand-shadow p-8 sm:p-10 border border-slate-100">
            <div className="mb-7">
              <h2
                className="text-2xl font-bold text-slate-900 tracking-tight"
                style={{ fontFamily: "Outfit" }}
              >
                Sign in to your account
              </h2>
              <p className="mt-1.5 text-sm text-slate-500">
                Use your work email to access the Content Hub.
              </p>
            </div>

            <form
              data-testid="login-form"
              onSubmit={handleSubmit}
              className="space-y-5"
              noValidate
            >
              <div>
                <label
                  htmlFor="email"
                  className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-2"
                >
                  Email Address
                </label>
                <input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@prephacker.com"
                  required
                  autoComplete="email"
                  className="w-full px-4 py-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-2"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    data-testid="login-password-input"
                    type={show ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                    className="w-full px-4 py-3 pr-11 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    data-testid="login-password-toggle"
                    aria-label="Toggle password visibility"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition"
                  >
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {error && errorKind === "locked" && (
                <div
                  data-testid="login-locked"
                  className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3.5 flex items-start gap-3"
                >
                  <Lock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-900 leading-relaxed">
                    <div className="font-semibold mb-0.5">Account locked</div>
                    {error}
                  </div>
                </div>
              )}
              {error && errorKind === "warn" && (
                <div
                  data-testid="login-warning"
                  className="rounded-lg bg-orange-50 border border-orange-200 px-4 py-3 flex items-start gap-3"
                >
                  <AlertTriangle className="w-4 h-4 text-orange-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-orange-800">{error}</div>
                </div>
              )}
              {error && errorKind === "error" && (
                <div
                  data-testid="login-error"
                  className="rounded-lg bg-red-50 border border-red-100 px-3 py-2.5 text-sm text-red-700"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                data-testid="login-submit-button"
                disabled={loading || !email || !password}
                className="group w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-orange-500 hover:bg-orange-600 active:bg-orange-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed text-white text-sm font-semibold transition-all"
              >
                {loading ? "Signing in…" : "Sign in"}
                {!loading && (
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                )}
              </button>
            </form>

            <div className="mt-7 pt-5 border-t border-slate-100 text-xs text-slate-400 text-center">
              Need access? Contact your Super Admin to provision an account.
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-slate-400 font-mono">
            Secured with role-based access control.
          </p>
        </div>
      </div>
    </div>
  );
}
